/**
 * AdminHome — the live admin home: count tiles (open leads / errors / active
 * cases / tickets), spec §4. Rendered as the Settings "Overview" (admin/owner).
 * Coded by kimi 2.6 to a conductor-verified spec; conductor-reviewed.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, AlertTriangle, Briefcase, LifeBuoy, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

interface HomeCounts {
  open_leads: number;
  open_errors: number;
  active_cases: number;
  open_tickets: number;
  tickets_available?: boolean;
}

export default function AdminHome() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<HomeCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const token = await getToken();
        const data = await api.get<HomeCounts>('/api/v1/admin/home-counts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setCounts(data);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-4 text-sm text-center" style={{ color: 'var(--color-text-muted)' }}>
        Couldn&apos;t load live counts.
      </p>
    );
  }

  const tiles = [
    { key: 'active_cases' as const, label: 'Active cases', icon: Briefcase, nav: '/admin/firms' },
    { key: 'open_leads' as const, label: 'Open leads', icon: Users, nav: '/admin/leads' },
    { key: 'open_errors' as const, label: 'Open errors', icon: AlertTriangle, nav: '/admin/error-logs' },
    { key: 'open_tickets' as const, label: 'Open tickets', icon: LifeBuoy, nav: '/settings?tab=tickets' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {tiles.map((tile) => {
        const value = counts ? counts[tile.key] : 0;
        const isErrorTile = tile.key === 'open_errors';
        // Errors use the semantic adverse/red token, NOT the brand accent — orange is
        // reserved for active/brand, so an error count in orange is a semantic collision
        // (K3 design gate 2026-10-09). The ONE red is the correct "something's wrong" signal.
        const numberColor = isErrorTile && value > 0 ? 'var(--color-status-adverse)' : 'var(--color-text-primary)';
        const disabled = tile.key === 'open_tickets' && counts?.tickets_available === false;

        const inner = (
          <>
            <tile.icon className="w-5 h-5 absolute top-4 right-4" style={{ color: 'var(--color-text-dim)' }} />
            <div
              className="mt-1"
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 28, color: numberColor, lineHeight: 1.2 }}
            >
              {value}
            </div>
            <div className="mt-2 cs-meta" style={{ color: 'var(--color-text-muted)' }}>{tile.label}</div>
            {disabled && (
              <div className="mt-1 text-xs" style={{ color: 'var(--color-text-dim)' }}>— not yet —</div>
            )}
          </>
        );

        if (disabled) {
          return (
            <div
              key={tile.key}
              className="relative rounded-xl p-5"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', cursor: 'default' }}
            >
              {inner}
            </div>
          );
        }

        return (
          <button
            key={tile.key}
            type="button"
            onClick={() => tile.nav && navigate(tile.nav)}
            className="relative rounded-xl p-5 text-left transition-opacity hover:opacity-90"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
}
